import React, { useRef, useState, useCallback } from 'react';
import { useEditorStore, type Section, type BackgroundPreset, type Episode } from '../../stores/editorStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useSectionImage } from '../../hooks/useSectionImage';

// Copy text to clipboard
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

// Helper to extract all context from a section (same as EpisodeStructureSidebar)
const getSectionContext = (section: Section, index: number): string => {
  const elements = section.elements
    .filter(e => e.type === 'text' || e.type === 'title-block')
    .map(e => e.content)
    .filter(Boolean)
    .join(' | ');

  const context = [
    `**${index + 1}. ${section.title || `Section ${index + 1}`}**`,
    elements && `Éléments: ${elements}`,
    section.talkingPoints && `Notes: ${section.talkingPoints}`
  ].filter(Boolean).join('\n  ');

  return context;
};

// Generate image prompt for a specific section
const generateSectionImagePrompt = (
  section: Section,
  index: number,
  episode: Episode,
  showName: string,
  theme: string
): string => {
  const sectionContext = getSectionContext(section, index);
  const allSectionsContext = episode.sections.map((s, i) => getSectionContext(s, i)).join('\n\n');

  return `Create a simple, clean illustration for section "${index + 1}. ${section.title || `Section ${index + 1}`}" of "${showName}".

THIS SECTION'S CONTENT:
${sectionContext}

FULL EPISODE CONTEXT (for reference):
${allSectionsContext}

Visual requirements:
- Simple, minimalist illustration style
- Dark theme with ${theme} accent colors
- Clean composition, not cluttered
- 16:9 aspect ratio
- The image should visually represent the main topic of this specific section
- NO TEXT in the image
- Focus on a SINGLE clear idea/concept
- Professional quality suitable for a news/tech presentation`;
};

// Solid base colors for each theme (YouTube-friendly, no gradient banding)
const baseColors: Record<string, string> = {
  'default': '#1a0a00',
  'blue': '#0a0a1a',
  'purple': '#0f0a1a',
  'green': '#0a1a0a',
  'red': '#1a0a0a',
  'neutral': '#0f0f0f',
};

const accentColors: Record<string, string> = {
  'default': '#ff6b00',
  'blue': '#3b82f6',
  'purple': '#8b5cf6',
  'green': '#22c55e',
  'red': '#ef4444',
  'neutral': '#6b7280',
};

// SVG noise texture as data URI
const noiseDataUri = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const themes: { id: BackgroundPreset; label: string; color: string }[] = [
  { id: 'default', label: 'Orange', color: 'bg-orange-600' },
  { id: 'blue', label: 'Bleu', color: 'bg-blue-600' },
  { id: 'purple', label: 'Violet', color: 'bg-purple-600' },
  { id: 'green', label: 'Vert', color: 'bg-green-600' },
  { id: 'red', label: 'Rouge', color: 'bg-red-600' },
  { id: 'neutral', label: 'Neutre', color: 'bg-gray-600' },
];

type SectionCardProps = {
  section: Section;
  index: number;
  isEditing: boolean;
  onRemove: () => void;
  canRemove: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
};

const SectionCard: React.FC<SectionCardProps> = ({ section, index, isEditing, onRemove, canRemove }) => {
  const { updateSectionById } = useEditorStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(section.title || '');
  const [showThemePicker, setShowThemePicker] = useState(false);
  
  const baseColor = baseColors[section.background] || baseColors['default'];
  const accentColor = accentColors[section.background] || accentColors['default'];

  const {
    isUploading,
    uploadProgress,
    uploadImage,
    validateFile,
  } = useSectionImage({
    imageUrl: section.imageUrl,
    onSuccess: (imageUrl) => {
      updateSectionById(section.id, { imageUrl });
    },
    onError: () => {},
    onRemove: () => {
      updateSectionById(section.id, { imageUrl: undefined });
    },
  });

  const handleImageClick = useCallback(() => {
    if (isEditing && !isUploading) {
      fileInputRef.current?.click();
    }
  }, [isEditing, isUploading]);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validation = validateFile(file);
    if (validation.valid) {
      await uploadImage(file);
    }
    e.target.value = '';
  }, [validateFile, uploadImage]);

  const handleRemoveImage = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    updateSectionById(section.id, { imageUrl: undefined });
  }, [section.id, updateSectionById]);

  const handleTitleClick = useCallback(() => {
    if (isEditing) {
      setTitleValue(section.title || '');
      setIsEditingTitle(true);
    }
  }, [isEditing, section.title]);

  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    updateSectionById(section.id, { title: titleValue || undefined });
  }, [section.id, titleValue, updateSectionById]);

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditingTitle(false);
      updateSectionById(section.id, { title: titleValue || undefined });
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
      setTitleValue(section.title || '');
    }
  }, [section.id, section.title, titleValue, updateSectionById]);

  const handleThemeSelect = useCallback((theme: BackgroundPreset) => {
    updateSectionById(section.id, { background: theme });
    setShowThemePicker(false);
  }, [section.id, updateSectionById]);

  return (
    <div 
      className="h-full rounded-2xl p-4 flex flex-col relative overflow-hidden group"
      style={{ 
        backgroundColor: baseColor,
        border: '3px solid rgba(255, 255, 255, 0.2)' 
      }}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Subtle color accent in corner */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 0% 0%, ${accentColor}20 0%, transparent 50%)`,
        }}
      />
      
      {/* Noise texture overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: noiseDataUri,
          backgroundRepeat: 'repeat',
        }}
      />

      {/* Vignette overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 100%)',
        }}
      />

      {/* Edit controls - top right */}
      {isEditing && (
        <div className="absolute top-2 right-2 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Theme picker button */}
          <button
            onClick={() => setShowThemePicker(!showThemePicker)}
            className="p-2 bg-black/50 hover:bg-black/70 rounded-lg text-white"
            title="Changer le thème"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          </button>
          
          {/* Delete button */}
          {canRemove && (
            <button
              onClick={onRemove}
              className="p-2 bg-red-500/70 hover:bg-red-500 rounded-lg text-white"
              title="Supprimer la section"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Theme picker dropdown */}
      {showThemePicker && (
        <div className="absolute top-12 right-2 z-30 bg-gray-800 rounded-lg p-2 shadow-xl border border-gray-700">
          <div className="grid grid-cols-3 gap-1">
            {themes.map((theme) => (
              <button
                key={theme.id}
                onClick={() => handleThemeSelect(theme.id)}
                className={`w-8 h-8 rounded ${theme.color} ${section.background === theme.id ? 'ring-2 ring-white' : ''} hover:scale-110 transition-transform`}
                title={theme.label}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Top 50%: Image */}
      <div 
        className={`h-1/2 relative z-10 ${isEditing ? 'cursor-pointer' : ''}`}
        onClick={handleImageClick}
      >
        {section.imageUrl ? (
          <div className="relative w-full h-full">
            <img 
              src={section.imageUrl} 
              alt={section.title || ''} 
              className="w-full h-full object-cover rounded-xl"
            />
            {isEditing && (
              <div className="absolute inset-0 bg-black/0 hover:bg-black/40 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-white text-lg font-medium">Changer l'image</span>
                <button
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white"
                  title="Supprimer l'image"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className={`w-full h-full bg-black/30 rounded-xl flex flex-col items-center justify-center ${isEditing ? 'hover:bg-black/50 border-2 border-dashed border-white/30 hover:border-white/50' : ''}`}>
            {isUploading ? (
              <div className="text-center">
                <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-orange-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
                <span className="text-white/70 text-sm">{uploadProgress}%</span>
              </div>
            ) : (
              <>
                <span className="text-6xl opacity-50 mb-2">{isEditing ? '📷' : '📰'}</span>
                {isEditing && <span className="text-white/50 text-sm">Cliquez pour ajouter</span>}
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Bottom 50%: Title */}
      <div 
        className={`h-1/2 flex flex-col items-center justify-center relative z-10 ${isEditing ? 'cursor-pointer' : ''}`}
        onClick={handleTitleClick}
      >
        {isEditingTitle ? (
          <input
            type="text"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            autoFocus
            className="bg-black/50 text-white font-bold text-center text-4xl px-4 py-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-orange-500"
            placeholder="Titre de la section"
          />
        ) : (
          <h3 className={`text-white font-bold text-center text-6xl px-4 ${isEditing ? 'hover:bg-white/10 rounded-lg py-2' : ''}`}>
            {section.title || (isEditing ? 'Cliquez pour éditer' : 'Sans titre')}
          </h3>
        )}
      </div>
      
      {/* Number at bottom */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center z-10">
        <span className="text-white/30 text-6xl font-bold">{index + 1}</span>
      </div>
    </div>
  );
};

interface SectionCardWrapperProps {
  section: Section;
  index: number;
  isEditing: boolean;
  onRemove: () => void;
  canRemove: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onGenerateImagePrompt: () => void;
  imagePromptCopied: boolean;
}

const SectionCardWrapper: React.FC<SectionCardWrapperProps> = ({
  section,
  index,
  isEditing,
  onRemove,
  canRemove,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onGenerateImagePrompt,
  imagePromptCopied
}) => {
  return (
    <div className="relative h-full">
      <div className="h-full min-w-0">
        <SectionCard
          section={section}
          index={index}
          isEditing={isEditing}
          onRemove={onRemove}
          canRemove={canRemove}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
        />
      </div>
      {isEditing && (
        <>
          <div className="absolute -bottom-14 left-0 right-0 flex justify-center gap-2">
            <button
              onClick={onMoveUp}
              disabled={!canMoveUp}
              className={`p-2 rounded-lg transition-colors ${
                canMoveUp
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-800 text-gray-600 cursor-not-allowed'
              }`}
              title="Déplacer vers la gauche"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={onMoveDown}
              disabled={!canMoveDown}
              className={`p-2 rounded-lg transition-colors ${
                canMoveDown
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-800 text-gray-600 cursor-not-allowed'
              }`}
              title="Déplacer vers la droite"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <button
            onClick={onGenerateImagePrompt}
            className="absolute -bottom-24 left-0 right-0 mx-auto w-max px-3 py-2 bg-orange-600/20 hover:bg-orange-600/30 border border-orange-500/50 text-orange-300 rounded-lg font-medium text-xs flex items-center justify-center gap-2 transition-all"
            title="Générer un prompt pour créer une image illustrant cette section"
          >
            <span>{imagePromptCopied ? '✓' : '🎨'}</span>
            <span>{imagePromptCopied ? 'Copié !' : 'Prompt génération image'}</span>
          </button>
        </>
      )}
    </div>
  );
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

interface IntroViewProps {
  isPresentation?: boolean;
}

export const IntroView: React.FC<IntroViewProps> = ({ isPresentation = false }) => {
  const { currentEpisode, mode, addSection, removeSection, canAddSection, canRemoveSection, moveSectionUp, moveSectionDown, canMoveSectionUp, canMoveSectionDown } = useEditorStore();
  const { settings } = useSettingsStore();
  const [sectionToDelete, setSectionToDelete] = useState<number | null>(null);
  const [copiedSectionIndex, setCopiedSectionIndex] = useState<number | null>(null);

  const sections = currentEpisode?.sections || [];
  const episodeDate = currentEpisode?.date ? formatDate(currentEpisode.date) : '';
  const isEditing = mode === 'edit' && !isPresentation;
  const canAdd = canAddSection();

  const handleRemoveSection = (index: number) => {
    setSectionToDelete(index);
  };

  const confirmDelete = () => {
    if (sectionToDelete !== null) {
      removeSection(sectionToDelete);
      setSectionToDelete(null);
    }
  };

  const cancelDelete = () => {
    setSectionToDelete(null);
  };

  const handleGenerateImagePrompt = async (index: number) => {
    if (!currentEpisode) return;

    const section = currentEpisode.sections[index];
    const resolvedShowName = currentEpisode.showTitle || settings.showName;
    const themeNames: Record<string, string> = {
      'default': 'orange',
      'blue': 'blue',
      'purple': 'purple',
      'green': 'green',
      'red': 'red',
      'neutral': 'gray'
    };
    const theme = themeNames[section.background] || 'orange';

    const prompt = generateSectionImagePrompt(section, index, currentEpisode, resolvedShowName, theme);

    const success = await copyToClipboard(prompt);
    if (success) {
      setCopiedSectionIndex(index);
      setTimeout(() => setCopiedSectionIndex(null), 1500);
    }
  };

  return (
    <div className="w-full h-full flex flex-col justify-center bg-black p-8">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-8">
        <span className="text-7xl font-bold" style={{ color: settings.accentColor }}>{currentEpisode?.showTitle || settings.showName}</span>
        {episodeDate && <span className="text-5xl text-gray-400">{episodeDate}</span>}
        {currentEpisode?.number != null && <span className="text-5xl text-gray-400">#{currentEpisode.number}</span>}
      </div>
      
      {/* Horizontal layout of section previews - 55% height */}
      <div className="flex gap-6" style={{ height: '55%' }}>
        {sections.length > 0 ? (
          sections.map((section, index) => (
            <div
              key={section.id}
              className="flex-1 min-w-0"
              style={{ maxWidth: `${100 / Math.max(sections.length, 1)}%` }}
            >
              <SectionCardWrapper
                section={section}
                index={index}
                isEditing={isEditing}
                onRemove={() => handleRemoveSection(index)}
                canRemove={canRemoveSection(index)}
                canMoveUp={canMoveSectionUp(index)}
                canMoveDown={canMoveSectionDown(index)}
                onMoveUp={() => moveSectionUp(index)}
                onMoveDown={() => moveSectionDown(index)}
                onGenerateImagePrompt={() => handleGenerateImagePrompt(index)}
                imagePromptCopied={copiedSectionIndex === index}
              />
            </div>
          ))
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-gray-400 text-xl">Aucune section</p>
            </div>
          </div>
        )}

      </div>

      {/* Add section button - below cards */}
      {isEditing && canAdd && (
        <div className="flex justify-center mt-28">
          <button
            onClick={() => addSection()}
            className="px-6 py-3 rounded-xl border-2 border-dashed border-gray-600 hover:border-orange-500 bg-black/20 hover:bg-orange-500/10 flex items-center gap-3 transition-colors group"
          >
            <span className="text-3xl text-gray-500 group-hover:text-orange-500">+</span>
            <span className="text-xl text-gray-500 group-hover:text-orange-500 font-medium">Ajouter une section</span>
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {sectionToDelete !== null && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-sm mx-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-gray-300 mb-4">
              Voulez-vous vraiment supprimer la section "
              {sections[sectionToDelete]?.title || 'sans titre'}" ?
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
